import {
  Alert,
  Item,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  useInfiniteScroll,
} from '@ibsys/kit';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useInfiniteUserGroups } from '@/entities/userGroups';
import { useAppNavigate } from '@/shared/lib';

export const UsersGroupsList: FC = () => {
  const { t } = useTranslation(['common', 'users']);
  const { id } = useParams<{ id?: string }>();
  const navigate = useAppNavigate();
  const viewportRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteUserGroups({ limit: 50 });
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 500,
    root: viewportRef,
  });

  const groups = data?.userGroups ?? [];

  const handleSelect = (groupId: number) => {
    navigate(`/user-groups/${groupId}`);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="w-full">
        <Alert className="text-muted-foreground p-4" title={t('users:noGroups')} />
      </div>
    );
  }

  return (
    <ScrollArea className={'pb-2'} viewPortProps={{ ref: viewportRef }}>
      <ul className="flex flex-col gap-4">
        {groups.map((group) => {
          const isActive = group.id === Number(id);
          return (
            <li key={group.id} onClick={() => handleSelect(group.id)}>
              <Item variant={isActive ? 'muted' : 'outline'} className="hover:bg-muted cursor-pointer">
                <ItemHeader>
                  <ItemTitle>{group.name}</ItemTitle>
                </ItemHeader>
              </Item>
            </li>
          );
        })}
        <Sentinel ref={sentinelRef}>
          {isFetchingNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
      </ul>
    </ScrollArea>
  );
};
